// Framework and third-party non-ui
import * as React from 'react';

import UniqueValueRenderer from '@arcgis/core/renderers/UniqueValueRenderer';

import { MapUtils } from 'utils';
import { getSymbolUrl } from 'utils/urls';

// Hooks, context, and constants
import { useMapContext, useAuthContext } from 'contexts';

// App components

// JSON & Styles
import { StyledMap } from './Map-styled';

// Third-party components (buttons, icons, etc.)

const Map = ({ user }) => {
  // State and Constants
  const mapRef = React.useRef();
  const MapContext = useMapContext();
  const AuthContext = useAuthContext();

  // Actions

  // Effects

  React.useEffect(() => {
    const { MapConfig, LayersConfig } = window.Pin2Flood;
    const mapItem = MapUtils.initMap(MapConfig['webmapId']);
    const { view, widgets } = MapUtils.initView(mapItem, mapRef, MapContext);

    mapItem.load().then().catch((error)=>{
      console.error(error)

      MapContext.dispatch({
        type: 'loadError',
        payload: {
          status: true,
          title: 'User Not Authorized',
          content: `The user '${AuthContext.state.user.id}' does not have access to view this application. To continue, sign out and try again with an authorized account.`,
          action: {
            title: "Sign Out",
            onClick: ()=>{AuthContext.signOut()}
          }
        },
      });
    })

    view.when(async () => {
      const layers = mapItem.layers.items;

      // Set renderer color by user
      const pinDropGroup = layers.find(
        (l) => l.title == LayersConfig.PINS.title
      );

      // TODO: Improve
      for (const l of pinDropGroup.layers.items) {
        await view.whenLayerView(l);

        // Owner, flood
        const ownerFloodSymbol = l.renderer.symbol.clone();
        ownerFloodSymbol.url = getSymbolUrl('pin-dark-flood-owner');

        // Owner, preplan
        const OwnerPreplanSymbol = l.renderer.symbol.clone();
        OwnerPreplanSymbol.url = getSymbolUrl('pin-dark-preplan-owner');

        ownerFloodSymbol.yoffset = 7.5;
        OwnerPreplanSymbol.yoffset = 7.5;

        const renderer = new UniqueValueRenderer({
          defaultSymbol: l.renderer.symbol,
          defaultLabel: `Non User ${LayersConfig.PINS.title}`,
          field: LayersConfig.PINS.fields.userid.name, //userid
          field2: LayersConfig.PINS.fields.pintype.name, //pintype
          fieldDelimiter: ',',

          uniqueValueInfos: [
            {
              value: [user.id, 'flood'].join(','),
              symbol: ownerFloodSymbol,
              label: `Owner Flood Edge ${LayersConfig.PINS.title}`,
            },
            {
              value: [user.id, 'preplan'].join(','),
              symbol: OwnerPreplanSymbol,
              label: `Owner Pre-Plan ${LayersConfig.PINS.title}`,
            },
          ],
          visualVariables: l.renderer.visualVariables,
        });

        l.renderer = renderer;
      }

      view.popup.visible = false;
      view.popup.watch('title', async () => {
        view.popup.visible = false;
        const floodLayer = view.layerViews.items.find(
          (lv) => lv.layer.title === LayersConfig.FLOOD_AREAS.title
        ).layer;

        const calculatePopupContent = async (feature, floodLayer) => {
          const compositeid =
            feature.attributes[LayersConfig.PINS.fields.compositeid.name];

          const params = {
            where: `${LayersConfig.FLOOD_AREAS.fields.compositeid.name}=${compositeid}`,
            returnGeometry: true,
            outFields: [
              LayersConfig.FLOOD_AREAS.fields.addresscount.name,
              LayersConfig.FLOOD_AREAS.fields.pinrelationship.name,
            ],
            f: 'json',
          };
          let query = floodLayer.createQuery();
          query = { ...query, ...params };

          let response = null;
          let floodFeatures = [];
          if (compositeid) {
            try{
              response = await floodLayer.queryFeatures(query);
              floodFeatures = response.features;
            }catch(error){
              console.error(error)
            }
          }

          return {
            id: feature.attributes[LayersConfig.PINS.fields.globalid.name],
            title: 'Details',
            feature,
            floods: floodFeatures,
            floodExtent: floodFeatures.length
              ? floodFeatures.map((f) => f.geometry)
              : [feature.geometry],
            addressCount: floodFeatures
              .map(
                (f) =>
                  f.attributes[
                    LayersConfig.FLOOD_AREAS.fields.addresscount.name
                  ]
              )
              .reduce((partialSum, i) => partialSum + i, 0),
            notes: feature.attributes[LayersConfig.PINS.fields.notes.name],
          };
        };

        if (view.popup.features) {
          const popupContent = await Promise.all(
            view.popup.features
              .filter(
                (f) => f.attributes.pintype !== null && f.geometry !== null
              )
              .map((feature) =>
                calculatePopupContent(feature, floodLayer, view.popup.title)
              )
          );

          MapContext.dispatch({ type: 'openPopup', payload: popupContent });
        }
      });

      MapContext.dispatch({
        type: 'setMapView',
        payload: { view, layers, widgets },
      });
    }, (error)=>{console.error(error)});
  }, []);

  return (
    <>
      <StyledMap ref={mapRef} data-testid="WebMap" />
    </>
  );
};

export default Map;
